import { DateTime } from 'luxon';
const EXTENSION_TYPE_MAP = {
    pdf: 'pdf',
    jpg: 'photo',
    jpeg: 'photo',
    png: 'photo',
    gif: 'photo',
    webp: 'photo',
    heic: 'photo',
    tif: 'photo',
    tiff: 'photo',
    dwg: 'cad',
    dxf: 'cad',
    rvt: 'cad',
    mp4: 'video',
    mov: 'video',
    avi: 'video',
    webm: 'video',
    mkv: 'video',
    m4v: 'video',
    xls: 'spreadsheet',
    xlsx: 'spreadsheet',
    csv: 'spreadsheet',
    doc: 'document',
    docx: 'document',
    txt: 'document',
    zip: 'zip'
};
const VIDEO_MIME_TYPES = {
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    webm: 'video/webm',
    mkv: 'video/x-matroska',
    m4v: 'video/x-m4v'
};
/** Lowercase filename extension without the dot, or null when the name has none. */
export function fileExtension(filename) {
    const match = /\.([a-z0-9]+)$/i.exec(filename);
    return match ? match[1].toLowerCase() : null;
}
/**
 * Splits a filename into an editable basename and a static extension for the inline `Name` cell
 * (E2E-27: "extension preserved"). Reuses `fileExtension`'s detection (same recognized-extension
 * rule as the rest of this module) but slices the *original* string so the extension keeps its
 * original case — `fileExtension` itself always lowercases its return value, which is right for
 * the type-detection keys it feeds but wrong for round-tripping display text.
 */
export function splitAttachmentName(filename) {
    const extension = fileExtension(filename);
    if (!extension)
        return { basename: filename, extension: null };
    const dotIndex = filename.length - extension.length - 1;
    return { basename: filename.slice(0, dotIndex), extension: filename.slice(dotIndex + 1) };
}
/**
 * Derives the file type from a filename's extension only.
 * Order: known extension → its key; unknown extension → null key (caller falls
 * back to the uppercased extension); no extension → the 'file' key.
 */
export function deriveFileType(filename) {
    var _a;
    const extension = fileExtension(filename);
    if (!extension)
        return { key: 'file', extension: null };
    return { key: (_a = EXTENSION_TYPE_MAP[extension]) !== null && _a !== void 0 ? _a : null, extension };
}
/**
 * Localized type label. `key === 'file'` and known keys resolve to
 * `file_type.<key>`; an unrecognized-but-present extension is shown verbatim
 * (uppercased), never localized — it's the file's own extension, not a phrase.
 */
export function fileTypeLabel(filename, translateFn) {
    const { key, extension } = deriveFileType(filename);
    if (key)
        return translateFn(`file_type.${key}`);
    return extension.toUpperCase();
}
/**
 * Stable, locale-independent identity for the "File type" filter and for
 * grouping present types (E2E-24) — a known key, or the raw uppercased
 * extension for an unrecognized one.
 */
export function fileTypeIdentity(filename) {
    const { key, extension } = deriveFileType(filename);
    return key !== null && key !== void 0 ? key : extension.toUpperCase();
}
/** Preview thumbnail/click behavior bucket — video is not a `FilePreviewThumb` kind. */
export function previewKindFor(filename) {
    const { key } = deriveFileType(filename);
    if (key === 'photo')
        return 'photo';
    if (key === 'pdf')
        return 'pdf';
    if (key === 'video')
        return 'video';
    return 'other';
}
/** MIME type for a video slide's `sources` — falls back to mp4 for an unmapped extension. */
export function videoMimeType(extension) {
    return (extension && VIDEO_MIME_TYPES[extension]) || 'video/mp4';
}
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * 1024;
const BYTES_PER_GB = BYTES_PER_MB * 1024;
function stripTrailingZero(value) {
    return value.endsWith('.0') ? value.slice(0, -2) : value;
}
/** `≥1 GB → N.N GB`; `≥1 MB → N.N MB` (trailing `.0` stripped); else `N KB`. */
export function formatFileSize(bytes) {
    if (bytes >= BYTES_PER_GB)
        return `${stripTrailingZero((bytes / BYTES_PER_GB).toFixed(1))} GB`;
    if (bytes >= BYTES_PER_MB)
        return `${stripTrailingZero((bytes / BYTES_PER_MB).toFixed(1))} MB`;
    return `${Math.round(bytes / BYTES_PER_KB)} KB`;
}
export function formatUploadedDate(iso) {
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toFormat('MM/dd/yyyy h:mm a') : '';
}
export function uploadedSortValue(iso) {
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.toMillis() : 0;
}
/** `[private ? 'hidden' : 'techs', ...(portal ? ['portal'] : [])]` — OR-matched by the Visibility filter. */
export function visibilityKeys(isPrivate, showOnCustomerPortal) {
    const keys = [isPrivate ? 'hidden' : 'techs'];
    if (showOnCustomerPortal)
        keys.push('portal');
    return keys;
}
/** `(private ? 0 : 1) + (portal ? 2 : 0)` — the exact sort key the design specifies. */
export function visibilitySortKey(isPrivate, showOnCustomerPortal) {
    return (isPrivate ? 0 : 1) + (showOnCustomerPortal ? 2 : 0);
}
export function visibilitySummaryKeyFor(isPrivate, showOnCustomerPortal) {
    if (isPrivate && !showOnCustomerPortal)
        return 'office_only';
    if (!isPrivate && !showOnCustomerPortal)
        return 'office_techs';
    if (isPrivate && showOnCustomerPortal)
        return 'office_portal';
    return 'office_techs_portal';
}
export function visibilitySummary(isPrivate, showOnCustomerPortal, translateFn) {
    return translateFn(`visibility_summary.${visibilitySummaryKeyFor(isPrivate, showOnCustomerPortal)}`);
}
